import React from 'react';
import PropTypes from 'prop-types';
import PageBar from '../../../../components/PageBar/PageBar';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import useTablePages from '../../../../hooks/useTablePages';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from './TestReports.columns';
import {
  GET_TEST_REPORT_IDS,
  GET_TEST_REPORTS,
  TestReportItem,
} from './TestReports.query';
import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import BackflowTestThirdPane from '../../../../components/BackflowTest/BackflowTestThirdPane';
import FilterBarTestReport, {
  defaultFilters,
  stringSearchFields,
  TestReportFilters,
} from './FilterBarTestReport';
import useMode from '../../../../hooks/useMode';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import TestReportsActions from './TestReportsActions';
import { flagOptions } from '../../../../components/BackflowTest/BackflowTestFlagsPicker';
import { getStringSearchOr } from '../../../../helpers/search';

const convertStoredTestResult = (testResult: string) => {
  let initialResultVal: string | undefined, finalResultVal: string | undefined;
  switch (testResult) {
    case 'FAIL':
    case 'PASS':
      finalResultVal = testResult;
      break;
    case 'FAILPASS':
      initialResultVal = 'FAIL';
      finalResultVal = 'PASS';
      break;
  }
  return {
    initialResult: initialResultVal,
    finalResult: finalResultVal,
  };
};

const convertFlags = (flags: string[]) => {
  const flagFilters = flagOptions
    .filter((option) => {
      return !!flags.find((value) => value === option.value);
    })
    .map((option) => {
      return option.filter;
    });

  if (flagFilters.length === 0) {
    return {};
  }

  if (flagFilters.length === 1) {
    return flagFilters[0];
  }

  return {
    or: flagFilters,
  };
};

const convertStoredString = (searchString: string) => ({
  or: getStringSearchOr(stringSearchFields, searchString),
});

const convertStoredDate = (
  dateStart: string | undefined,
  dateEnd: string | undefined,
  currentDateFilterField = 'dateTested'
) => {
  return {
    [currentDateFilterField]:
      dateStart && dateEnd
        ? {
            between: [dateStart, dateEnd],
          }
        : dateStart || dateEnd
          ? {
              gte: dateStart ? dateStart : undefined,
              lte: dateEnd ? dateEnd + 'T23:59:59.999Z' : undefined,
            }
          : undefined,
  };
};

export const convertFilters = (storedFilters: TestReportFilters) => {
  const query: { and: Record<string, any>[] } = { and: [] };
  if (storedFilters.waterSupplierStatus) {
    query.and.push({
      waterSupplierStatus: storedFilters.waterSupplierStatus,
    });
  }
  if (storedFilters.testResult) {
    query.and.push(convertStoredTestResult(storedFilters.testResult));
  }
  if (storedFilters.stringSearch) {
    query.and.push(convertStoredString(storedFilters.stringSearch));
  }
  if (storedFilters.flags) {
    query.and.push(convertFlags(storedFilters.flags));
  }
  if (storedFilters.dateStart || storedFilters.dateEnd) {
    query.and.push(
      convertStoredDate(
        storedFilters.dateStart,
        storedFilters.dateEnd,
        storedFilters.currentDateFilterField
      )
    );
  }

  if (query.and.length === 0) {
    delete (query as any).and;
  }

  return query;
};

type TestReportDetailParams = {
  id?: string;
};

type TestReportDetailProps = RouteComponentProps<TestReportDetailParams>;

/**
 * @component for jobs page
 */
const TestReports: React.FC<TestReportDetailProps> = ({ match }) => {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<TestReportFilters>(
      convertFilters,
      'testReports',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy(
    'testReports',
    availableColumns
  );

  const { loading, error, itemIds, selectedIds, setSelectedIds, itemLoader } =
    useTablePages<TestReportItem, 'jobId'>(
      'backflowTests',
      GET_TEST_REPORT_IDS,
      {
        where: queryFilters,
        limit: null,
        orderBy,
      },
      GET_TEST_REPORTS,
      (ids) => ({
        where: {
          jobId: ids,
        },
        limit: null,
        orderBy,
      }),
      'jobId'
    );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'testReports');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${useMode()}/test-reports`,
      itemIds,
      match.params.id,
      'jobId'
    );

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Backflow Test Approval" />
        <FilterBarTestReport
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No test reports found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<TestReportsActions jobIds={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <BackflowTestThirdPane
        jobId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
};

TestReports.propTypes = {
  match: PropTypes.any,
};

export default withRouter(TestReports);
